import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import styles from "./NavigationBar.module.css";
import { getCurrentUser, signOut } from 'aws-amplify/auth';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';

function NavigationBar({ isPortal = false }) {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [user, setUser] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const checkAuthentication = async () => {
      try {
        const userData = await getCurrentUser();
        setIsAuthenticated(true); // User is authenticated
        if (userData.attributes && isPortal) {
          setUser(`${userData.attributes.given_name} ${userData.attributes.family_name}`);
        }
      } catch {
        setIsAuthenticated(false); // User is not authenticated
      }
    };

    checkAuthentication();
  }, [isPortal]);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate("/");
    } catch (error) {
      console.error("Error signing out: ", error);
    }
  };

  return (
    <nav className={styles["navbar"]}>
      <div className={styles["nav-container"]}>
        <a href="https://presagetechnologies.com" className={styles["brand"]}>
          <img className={styles["logo"]} src="/assets/images/logo.webp" alt="Presage Logo" />
        </a>

        <button 
          aria-controls="navbar-menu" 
          aria-label="Toggle navigation menu"
          aria-expanded={mobileMenuOpen}
          className={styles["navbar-toggle"]}
          onClick={toggleMobileMenu}
        >
          {mobileMenuOpen ? <CloseIcon /> : <MenuIcon />}
        </button>

        <div className={`${styles["navbar-menu"]} ${mobileMenuOpen ? styles["navbar-menu-open"] : ""}`}>
          <div className={styles["navitem-container"]}>
            {isPortal ? (
              <>
                <Link
                  to="/"
                  className={`${styles["nav-item"]} ${location.pathname === "/" ? styles["active"] : ""}`}
                  onClick={closeMobileMenu}
                >
                  Dev Home
                </Link>
                <Link 
                  to="/portal/dashboard"
                  className={`${styles["nav-item"]} ${location.pathname === "/portal/dashboard" ? styles["active"] : ""}`}
                  onClick={closeMobileMenu}
                >
                  Dashboard
                </Link>
                <Link 
                  to="/portal/apps"
                  className={`${styles["nav-item"]} ${location.pathname === "/portal/apps" ? styles["active"] : ""}`}
                  onClick={closeMobileMenu}
                >
                  OAuth Registration
                </Link>
                <Link 
                  to="/quickstart"
                  className={`${styles["nav-item"]} ${location.pathname === "/quickstart" ? styles["active"] : ""}`}
                  onClick={closeMobileMenu}
                >
                  Quickstart
                </Link>
                <a 
                  className={styles["nav-item"]} 
                  href="https://docs.physiology.presagetech.com"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Docs
                </a>
                <a 
                  className={styles["nav-item"]} 
                  href="https://github.com/Presage-Security/SmartSpectra" 
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  GitHub
                </a>
                <a
                  className={styles["nav-item"]}
                  href="https://discord.com/invite/K7KNmuX7KT"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Discord
                </a>
                <Link 
                  to="/portal/settings"
                  className={`${styles["nav-item"]} ${location.pathname === "/portal/settings" ? styles["active"] : ""}`}
                  onClick={closeMobileMenu}
                >
                  Settings
                </Link>
              </>
            ) : (
              <>
                <Link
                  to="/"
                  className={`${styles["nav-item"]} ${location.pathname === "/" ? styles["active"] : ""}`}
                  onClick={closeMobileMenu}
                >
                  Dev Home
                </Link>
                <Link 
                  to="/quickstart"
                  className={`${styles["nav-item"]} ${location.pathname === "/quickstart" ? styles["active"] : ""}`}
                  onClick={closeMobileMenu}
                >
                  Quickstart
                </Link>
                <a 
                  className={styles["nav-item"]} 
                  href="https://docs.physiology.presagetech.com"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Docs
                </a>
                <a 
                  className={styles["nav-item"]} 
                  href="https://github.com/Presage-Security/SmartSpectra" 
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  GitHub
                </a>
                <a
                  className={styles["nav-item"]}
                  href="https://discord.com/invite/K7KNmuX7KT"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Discord
                </a>
              </>
            )}
          </div>

          <div className={styles["cta-container"]}>
            {isPortal ? (
              <>
                {user && (
                  <span className={styles["user-name"]}>{user}</span>
                )}
                <button 
                  onClick={handleSignOut}
                  className={`${styles["nav-item"]} ${styles["cta-btn"]}`}
                >
                  Logout
                </button>
              </>
            ) : (
              <>
                {!isAuthenticated ? (
                  <>
                    <Link 
                      to="/auth/login"
                      className={`${styles["nav-item"]} ${styles["login-btn"]}`}
                      onClick={closeMobileMenu}
                    >
                      Login
                    </Link>
                    <Link 
                      to="/auth/register"
                      className={`${styles["nav-item"]} ${styles["cta-btn"]}`}
                      onClick={closeMobileMenu}
                    >
                      Register
                    </Link>
                  </>
                ) : (
                  <Link 
                    to="/portal"
                    className={`${styles["nav-item"]} ${styles["cta-btn"]}`}
                    onClick={closeMobileMenu}
                  >
                    Portal
                  </Link>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
}

export default NavigationBar;